import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { ETaxBlockedDocument } from "../../types/e-tax.type";
import { numberWithCommas } from "../../utils";

export interface SendProblem {
  /** Bills the gate refused in the rejected chunk. Empty when the chunk failed for another reason. */
  refusals: ETaxBlockedDocument[];
  /** Bills in chunks the backend had already accepted before the failure. */
  alreadySentCount: number;
  /** Bills that were not sent: the rejected chunk and every chunk after it. */
  notSentCount: number;
}

interface SendProblemModalProps {
  problem: SendProblem | null;
  /** What was being sent; the bill list's "บิล" by default. */
  documentNoun?: string;
  onClose: () => void;
}

/**
 * A manual send that did not fully go through: the gate refused the request (it rejects the
 * whole request for a single refused bill), or a later chunk of a send-all failed after
 * earlier chunks were already written.
 */
export default function SendProblemModal({
  problem,
  documentNoun = "บิล",
  onClose,
}: SendProblemModalProps) {
  const refused = problem?.refusals.length ?? 0;
  return (
    <Dialog open={Boolean(problem)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        {refused > 0
          ? `ส่งไม่ได้: มี${documentNoun}ไม่ผ่านการตรวจสอบ`
          : "ส่งเอกสาร E-Tax ไม่ครบ"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {problem && (
          <>
            {problem.alreadySentCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                ชุดก่อนหน้าส่งไปแล้ว {numberWithCommas(problem.alreadySentCount)} ใบ
                ไม่ต้องส่งซ้ำ รายการด้านหลังอัปเดตแล้ว
              </Alert>
            )}

            {refused > 0 ? (
              <Typography variant="body2" sx={{ mb: 2 }}>
                ระบบไม่ส่ง{documentNoun}ในชุดนี้เลย ({numberWithCommas(problem.notSentCount)} ใบ)
                เพราะมี{documentNoun} {numberWithCommas(refused)} ใบไม่ผ่านการตรวจสอบ{" "}
                {documentNoun}เหล่านี้ถูกเอาออกจากรายการที่เลือกแล้ว กรุณากดส่งใหม่อีกครั้ง
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }}>
                ยังไม่ได้ส่งอีก {numberWithCommas(problem.notSentCount)} ใบ
                กรุณาตรวจสอบรายการแล้วกดส่งใหม่อีกครั้ง
              </Typography>
            )}

            {refused > 0 && (
              <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>เลขที่{documentNoun}</TableCell>
                      <TableCell>เหตุผล</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {problem.refusals.map((row) => (
                      <TableRow key={row.transaction_uuid}>
                        <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                          {row.transaction_no || row.transaction_uuid}
                        </TableCell>
                        <TableCell>
                          {row.messages.length > 0 ? (
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                              {row.messages.map((message) => (
                                <li key={message}>{message}</li>
                              ))}
                            </Box>
                          ) : (
                            row.reason_codes.join(", ") || "-"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="contained" onClick={onClose}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
